/*
 * Copyright (c) 2026. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* planbordscherm.
* 19-11-2020 - Jelmer Jellema - Spin in het Web B.V.
*/

import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    Renderer2,
    ViewChild,
    ViewChildren
} from '@angular/core';
import {Appscherm} from '../abstracts/appscherm';
import moment from 'moment';
import {cloneDeep, keyBy, sortBy} from 'lodash-es';
import {hash} from '../../app/types';
import {interval} from 'rxjs';
import {Project} from '../../models/project';
import {Personeel, Personeelgroep} from '../../models/personeel';
import {Functie} from '../../models/functie';
import {Sihwsemaphore} from 'sihw-ng/semaphore/sihwsemaphore';
import {Planitem} from '../../models/planitem';
import {PlanitemFunction} from '../../models/planitem_function';
import {ProjectFunctie} from '../../models/project_functie';
import {PersoneelPlanitem} from '../../models/personeel_planitem';
import {PersoneelFunctie} from '../../models/personeel_functie';
import {Sihwlog, LocalConfig} from 'sihw-ngx';
import {Leverancier} from '../../models/leverancier';
import {PersoneelDatumcomment} from '../../models/personeel_datumcomment';
import {ProjectDatumcomment} from '../../models/project_datumcomment';
import {FunctieExact} from '../../models/functie_exact';
import {PersoneelAfwezigheid} from '../../models/personeel_afwezigheid';
import {ApiService} from '../../services/api/api.service';
import {PersoneelLabel} from '../../models/personeel_label';
import {FiltertoolFactoryService} from '../../services/filtertool/filtertool-factory.service';
import {FiltertoolInstantie} from '../../services/filtertool/filtertool-instantie';
import {KeyedTimeout} from 'sihw-ng/keyedtimeout/keyedtimeout';
import {Werkmaatschappij} from '../../models/werkmaatschappij';
import {ExterneProjectInfo} from './planitemedit';

////////////////////////////////////// HELPERS ////////////////////////////////////////////////////////////////


//so they can be referenced in the planboard

@Component({
    selector: 'planblock',
    templateUrl: './planblock.html',
    styleUrls: ['./planblock.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class Planblock {
    @Input() planitem: Planitem;
    @Input() weergave: 'project' | 'invulling' | 'invulling_planning'; //type weergave van het bord
    @Input() visual: boolean = false; //only show visual
    @Input() virtueel: boolean = false; //true: de user kan dit item doen, maar doet het niet.
    @Input() toonkritiek: boolean = true; //als false, dan geen kritiekhints. Een leverancier krijgt hem ook te zien: de kritiek zelf is voor hem beperkt tot zijn eigen mensen, zie Planitem.setKritiekLeverancierfilter
    @ViewChild('hetblokje') element: ElementRef;
    @Output('edit') editEvent = new EventEmitter<void>(); //event voor addremoveknop
    @Output('addremove') addRemoveEvent = new EventEmitter<void>(); //event voor addremoveknop
    public noticeme = false; //to get attention, see attention()
    private log: Sihwlog;
    protected isLeverancier: boolean = false; //1 keer opvragen
    /** Change detection helper voor OnPush: expliciet hertekenen bij externe aanroep. */
    private readonly cd = inject(ChangeDetectorRef);

    constructor(private api: ApiService) {
        this.log = new Sihwlog('debug', 'Planblock');
        this.isLeverancier = this.api.can('planitem', 'leverancier');
    }

    @HostBinding('class.visual') get class_visual() {
        return this.visual;
    } //zet de class ook op het planitem

    get nativeElement() {
        return this.element?.nativeElement;
    }

    public get hoogte(): number {
        return this.nativeElement?.offsetHeight || 0;
    }

    /**
     * Make sure we are noticed
     */
    public attention() {
        this.noticeme = true;
        //will become false on the end of the animation
        // OnPush: bij aanroep buiten Angular events expliciet hertekenen
        this.cd.markForCheck();
    }

    /**
     * Klik voor edit: sturen we door
     */
    public onEdit() {
        //bij invulling_planning hebben we alleen de kleine knopjes
        if (this.weergave !== 'invulling_planning') {
            this.editEvent.next();
        }
    }

    /**
     * Klik op plus of min. We vuren een event
     */
    public onAddRemove() {
        this.addRemoveEvent.next();
    }

    /**
     * Check of een personeelslid van een andere werkmaatschappij is in een partner-context.
     */
    public isAndereWM(personeel: Personeel): boolean {
        if (!this.planitem.externe_werkmaatschappij) {
            return false;
        }
        if (this.isLeverancier) {
            return false;
        }
        return personeel && personeel.werkmaatschappij_id !== this.api.werkmaatschappij.id;
    }

}

/////////////////////// planblockdag,
// een dagblok in een planblockrij, voor meten hoogte van de rij (zie hieronder)
@Directive({
    selector: '[planblockdag]'
})
export class PlanblockDag {
    @ContentChildren(Planblock, {descendants: true}) planblocks: QueryList<Planblock>;
    @ContentChild('commentblock', {static: false}) commentblock: ElementRef; //om mee te nemen in de hoogte
    @Input() datestr: string;

    get heeftComment(): boolean {
        return !!this.commentblock?.nativeElement;
    }

    /**
     * Bereken de hoogte die dit item moet hebben gegeven de hoogte hier en eventueel overlappende blokken in gisteren
     * @param prevdag
     */
    berekenHoogte(prevdag?: PlanblockDag): number {
        let h = Math.max(...this.planblocks.map(pb => pb.hoogte)); //van onze planblokken
        if (this.heeftComment) {
            //die moet erbij, maar het maakt dan ook uit of gisteren overlappende planblokken heeft
            if (prevdag) {
                for (let blok of prevdag.planblocks) {
                    if (blok.planitem.endtime <= blok.planitem.starttime) {
                        //dus valt hij in de volgende dag
                        h = Math.max(h, blok.hoogte);
                    }
                }
            }
            //nu nog het comment zelf erbij
            h += this.commentblock.nativeElement?.offsetHeight || 0;
        }
        //altijd padding
        h += 16;
        //en een minimum
        h = Math.max(h, 42);
        return h;
    }
}

///////////////////////// planitemrij, in hoogte verstelbaar door de planblockdag items hierboven
@Directive({
    selector: '[planblockrij]'
})
export class PlanblockRij implements OnDestroy {
    @ContentChildren(PlanblockDag, {descendants: true}) dagen: QueryList<PlanblockDag>; //deze bevragen we om de hoogte
    @ContentChild('naamcel', {static: false}) naamcel?: ElementRef; //referentie naar de naamcel binnen deze rij
    private inhoudWijziging: MutationObserver;

    /**
     * Als een rij wil herberekenen dan vraagt hij dit aan de class, om dit in goede banen te leiden
     */
    private static herberekenAgenda: PlanblockRij[] = [];
    private static herberekenTimer: number = null;

    protected static planHerberekenen(rij: PlanblockRij) {
        if (!this.herberekenAgenda.includes(rij)) {
            this.herberekenAgenda.push(rij);
            if (!this.herberekenTimer) {
                this.herberekenTimer = window.setTimeout(() => {
                    this.doeHerberekenen();
                }, 50);
            }
        }
    }

    static clearHerberekenAgenda() {
        this.herberekenAgenda = [];
        if (this.herberekenTimer) {
            window.clearTimeout(this.herberekenTimer);
            this.herberekenTimer = null;
        }
    }

    /**
     * Laat de eerste rij herberekenen
     * @private
     */
    private static doeHerberekenen() {
        let rij: PlanblockRij;
        while ((rij = this.herberekenAgenda.shift())) {
            if (rij.bestaat) {
                //berekenen
                rij.herberekenHoogte();
                //nieuwe timer
                this.herberekenTimer = requestAnimationFrame(() => {
                    this.doeHerberekenen();
                });
                return; //meer niet
            }
            //anders doorvallen naar volgende
        }
        //hier is er geen rij
        this.herberekenTimer = null; //klaar
    }

    constructor(public element: ElementRef, private renderer: Renderer2) {
        //let op alle inhoudswijziging, om de hoogte te berekenen
        this.inhoudWijziging = new MutationObserver((_mutations: MutationRecord[]) => {
            // this.debounceHoogte(); //herberekenen
            PlanblockRij.planHerberekenen(this);
        });
        this.inhoudWijziging.observe(this.element.nativeElement, {childList: true, subtree: true}); //alle wijzigingen qua componenten
    }

    get bestaat(): boolean {
        return document.body.contains(this.element.nativeElement);
    }

    ngOnDestroy() {
        this.inhoudWijziging.disconnect();
        //PlanblockRij.stopHerbereken(this);
    }

    /**
     * Bij init of op verzoek: opnieuw berekenen van de hoogte
     */
    herberekenHoogte() {
        //we lopen er gewoon doorheen, gesorteerd, want we moeten rekening houden met
        //de vorige dag en planblokken die deze dag in steken. Die moeten hier niet botsen met comments
        let hoogte = 0;
        const sorteerdagen = this.dagen.toArray();
        sortBy(sorteerdagen, 'datestr');
        let prevdag: PlanblockDag = null;
        for (let dag of sorteerdagen) {
            let daghoogte = dag.berekenHoogte(prevdag);
            if (daghoogte > hoogte) {
                hoogte = daghoogte;
            }
            prevdag = dag;
        }
        // Neem ook de hoogte van de naamcel mee (kan meerdere regels tekst bevatten)
        const naamcelEl: HTMLElement | undefined = this.naamcel?.nativeElement as HTMLElement | undefined;
        if (naamcelEl && typeof naamcelEl.offsetHeight === 'number') {
            hoogte = Math.max(hoogte, naamcelEl.offsetHeight);
        }
        this.renderer.setStyle(this.element.nativeElement, 'height', `${hoogte}px`);
    }
}


////////////////////////////////////// PLANBOARD //////////////////////////////////////////////////////
@Component({
    selector: 'scherm-planbord',
    templateUrl: './planbord.html',
    styleUrls: ['./planbord.scss'],
})
export class Planbordscherm extends Appscherm implements OnInit {
    @ViewChild('planbordscherm', {static: true}) scherm: ElementRef;
    @ViewChild('planbordscroller', {static: true}) planbordscroller: ElementRef;
    @ViewChild('planbordheader', {static: true}) planbordheader: ElementRef;
    @ViewChild('naamcel', {static: true}) naamcel: ElementRef; //for calculation using fixed width
    @ViewChildren(Planblock) planblocks: QueryList<Planblock>;

    _starttitle = 'Planbord';
    icon = 'fas fa-th';
    openMaximized = true;

    datums: moment.Moment[] = []; //af te beelden datums, als moment
    datumstrings: string[] = []; //af te beelden datums, als mysql-string
    projectdata: Project[] = []; //projecten en subprojecten, met veel _tags. Voor de weergave 'project'
    //we kunnen ook per klant openklappen
    functievolgorde = null; //gebruikerstag voor functievolgorde
    klanten: {
        klantnaam: string,
        projecten: Project[], //objecten uit projectdata
        projectid: hash<boolean> //snel zoeken op project
    }[] = [];
    itemsPerDateGefilterd: hash<Planitem[]> = {};
    // Lookups voor werkmaatschappijen, tbv naamweergave bij externe projecten
    werkmaatschappijLijst: Werkmaatschappij[] = [];
    werkmaatschappijen: hash<Werkmaatschappij> = {};
    /** Alle planitems per datum, ongeacht zichtbaarheid in de UI; gebruikt voor overlap/ATW-berekeningen. */
    itemsPerDateAlles: hash<Planitem[]> = {};
    personeel: hash<Personeel> = {}; //personeel met afwezigheid in de planbordperiode
    personeellijst: Personeel[] = []; //zelfde objecten als in de hash, als array
    personeeldata: Personeel[] = []; //Personeel met planitems in _tags. We gebruiken deze, niet de directe lookup, tbv behoud van tags
    verwijderdeItems: Planitem[] = []; //gevuld bij laden, zodat we een log kunnen laten zien

    //lookups:
    functies: hash<Functie> = {};
    leverancierlijst: Leverancier[] = [];
    leveranciers: hash<Leverancier> = {};
    projectlijst: Project[] = [];
    projecten: hash<Project> = {};
    personeeldatumcomments: hash<hash<PersoneelDatumcomment>> = {}; //op personeel_id op datum, zie haalComments()
    projectdatumcomments: hash<hash<hash<ProjectDatumcomment>>> = {}; //comments per project per functietag per datum
    personeellabels: string[] = [];
    /** Hash met extra projectinfo voor externe planitems (werkmaatschappij, project id, naam, omschrijving) */
    externeProjectInfo: hash<ExterneProjectInfo> = {};

    datumBereikLijst: any[] = [ //aantal dagen
        {dagen: 4, waarde: '4 dagen'},
        {dagen: 7, waarde: '1 week'},
        {dagen: 14, waarde: '2 weken'},
        {dagen: 21, waarde: '3 weken'},
        {dagen: 28, waarde: '4 weken'},
        {dagen: 35, waarde: '5 weken'},
        {dagen: 42, waarde: '6 weken'},


    ];
    datumbereik: number = 7;
    begindatum: moment.Moment;
    einddatum: moment.Moment;
    public moment = moment; //this.moment

    public nu: moment.Moment = moment(); //wordt gezet in bepaalNu elke minuut
    public nustring: string = moment().format('YYY-MM-DD HH:mm:ss');
    public nudate: string = moment().format('YYYY-MM-DD');
    public nuLijn: number;

    public canAddPlanitems: boolean = false; //mogen we planitems toevoegen
    public canComment: boolean = false; //mogen we comments toevoegen
    public magPersoneel: boolean = false; //mag er personeel gesaved worden door de huidige gebruiker?
    public volLezen = false; //volledige leestoestemming? (heeft full ook)
    public isLeverancier = false; //speciale leveranciertoestemming?
    /**
     * Vlag die aangeeft of filteren toegestaan is voor de huidige gebruiker. Alles-of-niets.
     */
    public magFilteren: boolean = false;

    ux = {
        daterange: <any> null, //model voor daterangepicker
        klantnaamOpened: <hash<boolean>> {},
        projectOpened: <hash<boolean>> {},
        groepOpened: <hash<boolean>> {},
        actieveGroepen: <Personeelgroep[]> [],
        weergave: <'project' | 'invulling' | 'invulling_planning'> 'project',
        //filters: zowel voor weergave als voor planning....
        projectfilter: <number> null,
        leverancierfilter: <number> null,
        /** Schakelaar: bepaalt of de filtertool-instellingen worden toegepast op het planbord. */
        filterActief: <boolean> true,
        dragging: <boolean> false //is men aan het draggen? Dan verbergen we dingen
    };


    public dataGewijzigd = false; //is er in het be iets gewijzigd? Dan tonen we de reloadknop


    ////////////// drag
    start_x: number;
    start_y: number;
    start_top: number;
    start_left: number;

    private semaphore: Sihwsemaphore = inject(Sihwsemaphore);
    private debounce: KeyedTimeout = new KeyedTimeout();

    private readonly filtertoolFactory = inject(FiltertoolFactoryService);
    /**
     * Doel: de geregistreerde filtertool-instantie die bij dit planbord hoort
     */
    public filtertool: FiltertoolInstantie = null;

    constructor() {
        super('debug');
        //is er een bepaalde weergave in de config afgedwongen?
        const config = new LocalConfig();
        this.ux.weergave = config.$('planbordview', 'project');
    }

    async ngOnInit() {
        //registreer eventueel events van de filtertool
        if (!this.filtertool) {
            // wordt in try-blok hieronder gezet; de subscribe doen we daarna
        }
        try {
            // Bepaal of filteren is toegestaan
            this.magFilteren = this.filtertoolFactory.canFiltertool();
            if (this.magFilteren) {
                // Haal de opgeslagen status van filterActief uit sessionStorage
                const opgeslagenFilterActief = sessionStorage.getItem('planbord.filterActief');
                if (opgeslagenFilterActief !== null) {
                    this.ux.filterActief = opgeslagenFilterActief === 'true';
                }

                // Filtertool: verkrijg een instantie via de factory, inclusief eventueel opgeslagen status
                // Elk geopend planbord beschikt over een eigen filtercontext; context hier expliciet op 'planbord'
                this.filtertool = this.filtertoolFactory.leesHuidigeFilter('planbord');
                if (this.filtertool) {
                    // Sla elke boomwijziging direct op via de factory
                    if ((this.filtertool as any).boomGewijzigd$) {
                        this.subscription(this.filtertool.boomGewijzigd$.subscribe(() => {
                            // Sla wijzigingen op onder de expliciete planbord-context
                            this.filtertoolFactory.saveHuidigeFilter(this.filtertool?.id, 'planbord');
                        }));
                    }
                }
                // Luister naar toepassen van de filtertool en zet filtertoepassing aan/uit afhankelijk van de boominhoud
                if (this.filtertool && (this.filtertool as any).toegepast$) {
                    this.subscription(this.filtertool.toegepast$.subscribe(() => {
                        /**
                         * Bij toepassen bepalen we of de filter inhoud heeft. Als de boom leeg is (geen clausules),
                         * dan schakelen we het toepassen uit. Heeft de boom wel kinderen, dan zetten we toepassen aan.
                         */
                        this.ux.filterActief = !!(this.filtertool?.boom?.kinderen?.length);
                        sessionStorage.setItem('planbord.filterActief', this.ux.filterActief ? 'true' : 'false');
                        void this.bouwPlanbord();
                    }));
                }
            } else {
                // Filteren niet toegestaan: zorg dat er niets wordt toegepast of getoond
                this.filtertool = null;
                this.ux.filterActief = false;
            }
            //we halen alle functievolgordes op voor de ingelogde wm
            this.functievolgorde = (await this.api.tag(`pl_functievolgorde_${this.api.werkmaatschappij.id}`)) || {};

            //can we add items?
            this.canAddPlanitems = this.api.can('planitem', ['full', 'project']);
            this.canComment = this.api.can('project_datumcomment', ['full', 'project']);
            this.magPersoneel = this.canAddPlanitems && this.api.can('personeelvast_planitem', 'full');
            this.volLezen = this.api.can('planitem', 'read');
            //alleen leverancier? Die heeft geen read-toestemming
            this.isLeverancier = (!this.volLezen) && this.api.can('planitem', 'leverancier'); //speciale permissie
            this.begindatum = moment().isoWeekday(1).startOf('day');

            //fix weergave
            if ((this.ux.weergave === 'invulling' && (!this.volLezen)) || (this.ux.weergave === 'invulling_planning' && (!this.magPersoneel))) {
                //basis
                this.ux.weergave = 'project';
            }

            await this.haalBasisdata();

            await this.bouwPlanbord();

            //zet een timer voor this.nu en de nulijn
            //doen we best vaak, maar niet realtime in templates met moment() rommelen, dan gebeurt het heel erg vaak
            this.subscription(interval(60 * 1000).subscribe(_ => {
                this.bepaalNu();
            }));
            //en maak het bord opnieuw als er iets wijzigt
            this.subscription(this.api.onDatawijziging([Project, Planitem, PlanitemFunction, Personeel, Functie, PersoneelAfwezigheid]).subscribe(data => {
                    if (data.self) {
                        //dit komt van ons. Uitvoeren met een debouncer (want met reeksen kunnen het er veel zijn)
                        this.debounce.set('datawijziging_reload', async () => {
                            await this.fullReload();
                        }, 200);
                    } else if (!this.dataGewijzigd) {
                        this.dataGewijzigd = true; //dus een knop om bij te werken
                        //this.api.toast('Er is mogelijk relevante data gewijzigd. Werk desgewenst het planbord bij met de knop linksonder');
                    }
                }
            ));

            this.subscription(this.api.onDatawijziging([PersoneelDatumcomment]).subscribe((data: any) => {
                void this.personeelcommentUpdate(data);
            }));

            this.subscription(this.api.onDatawijziging([ProjectDatumcomment]).subscribe((data: any) => {
                void this.projectcommentUpdate(data);
            }));
        } catch (e) {
            this.log.error(e);
            this.closeWindow();
        }
    }


    async reloadKnop() {
        await this.fullReload();
    }

    /**
     * Opent het Filtertoolscherm als afhankelijk venster van dit planbord.
     * Het scherm ontvangt de filterId van de bij dit planbord behorende filtertool-instantie.
     */
    public openFiltertool(): void {
        void this.createWindow('Filtertoolscherm', {filterId: this.filtertool?.id}, true, true);
    }

    // Public methods
    /**
     * Verwerkt het togglen van de filtertoepassing-schakelaar en bouwt het planbord opnieuw op.
     * Deze herbouw vindt altijd plaats, ongeacht of de schakelaar aan of uit gezet wordt.
     */
    public filterActiefGewijzigd(): void {
        sessionStorage.setItem('planbord.filterActief', this.ux.filterActief ? 'true' : 'false');
        void this.bouwPlanbord();
    }

    /**
     * Geeft aan of de filtertoepassing geactiveerd is. Te gebruiken bij het conditioneel toepassen van filterlogica.
     */
    public get isFilterActief(): boolean {
        return !!this.ux.filterActief;
    }

    /**
     * Geeft aan of er een actief filter gebruikt wordt op het planbord.
     * Een filter geldt als actief als de toggle aan staat en de toegepaste filterboom
     * van de filtertool ten minste één clausule bevat.
     */
    public get heeftActiefFilter(): boolean {
        return !!(this.isFilterActief && this.filtertool?.boom?.kinderen?.length);
    }


    /**
     * Switch de periode
     */
    switchPeriode() {
        //reset the date and rebuild for the new period
        this.setBegindatum(this.begindatum, true); //force rebuild, monday first if week
    }

    /**
     * Schuif de begindatum 7 dagen vooruit of achteruit
     * @param richting
     */
    schuifBegindatum(richting) {
        //we can't use datumbereik if it's zero
        let numberOfDays = this.einddatum.diff(this.begindatum, 'days') + 1;
        this.setBegindatum(this.begindatum.clone().add(richting * numberOfDays, 'days').startOf('day'));
    };

    /**
     * Zet de planning weer op nu
     */
    begindatumNu() {
        this.setBegindatum(moment());
    }

    /**
     * Toon een bepaalde datum als begindatum, maar wel goed afgesplitst
     * @param datum
     * @param forceerHerbouw Als gegeven en true, dan wordt het planbord herbouwt, ook als de begindatum al goed stond
     */
    setBegindatum(datum: moment.Moment, forceerHerbouw: boolean = false) {
        if ((!forceerHerbouw) && this.begindatum && this.begindatum.isSame(datum, 'day')) {
            return; //staat al goed
        }
        let numberOfDays = this.einddatum.diff(this.begindatum, 'days'); //if datumbereik = 0
        this.begindatum = datum.clone();
        if (this.datumbereik > 0 && (this.datumbereik % 7 === 0)) {
            this.begindatum.isoWeekday(1); //maandag, want we beelden hele weken af
        }
        this.begindatum.startOf('day');
        if (!this.datumbereik) {
            this.einddatum = this.begindatum.clone().add(numberOfDays, 'days'); //make sure it's the same range
        }

        this.resetScroll();
        void this.bouwPlanbord();
    };

    setFreeDaterange() {
        //set the daterange, based on the free selector
        if (this.ux.daterange?.length === 2) {
            this.begindatum = moment(this.ux.daterange[0]);
            this.einddatum = moment(this.ux.daterange[1]);
            if (this.begindatum.isAfter(this.einddatum)) {
                //even swappen:
                let nieuwbegin = this.einddatum;
                this.einddatum = this.begindatum;
                this.begindatum = nieuwbegin;
            }
            this.datumbereik = 0; //free
            void this.bouwPlanbord();
            this.resetScroll();
        }
    }

    async haalBasisdata() {
        //functie met functie-exact, maar niet verplicht
        //(bijv vanwege NBB-functie in bamimport)
        this.functies = await Functie.getLookup(Project, [FunctieExact], [
            [FunctieExact,
                {
                    required: false
                }
            ]
        ]);


        if (this.api.can('leverancier', 'read')) {
            this.leverancierlijst = await Leverancier.getAll();
            this.leveranciers = keyBy(this.leverancierlijst, 'id');
        } else {
            this.leverancierlijst = [];
            this.leveranciers = {};
        }


        //nieuw ivm versnelling planbord: we halen hier al alle projecten op, gewoon alles voor deze wm
        this.projectlijst = await Project.getAll(
            [ProjectFunctie],
            [
                [Project, {
                    where: {
                        werkmaatschappij_id: this.api.werkmaatschappij.id
                    }
                }],
                [ProjectFunctie, {required: true}] //moet wbs hebben
            ]
        ); //alleen de basics voor goede caching
        this.projecten = keyBy(this.projectlijst, 'id'); //lookup

        // haal werkmaatschappijen op voor naamweergave bij externe projecten
        try {
            this.werkmaatschappijLijst = await Werkmaatschappij.getAll([], [[Werkmaatschappij, {attributes: ['id', 'naam']}]], null, Project);
            this.werkmaatschappijen = keyBy(this.werkmaatschappijLijst, 'id');
        } catch (e) {
            this.werkmaatschappijLijst = [];
            this.werkmaatschappijen = {} as any;
        }

        //zo ook al het personeel, maar zonder afwezigheid
        //ook uit andere werkmaatschappijen, dat mag van het backend bij deze lookup

        this.personeellijst = await Personeel.getAll([PersoneelFunctie, PersoneelLabel], [], null, Project);
        this.personeel = keyBy(this.personeellijst, 'id');


    }

    /**
     * Opzetten volledige planborddata.
     */
    async bouwPlanbord() {
        try {
            // noinspection DuplicatedCode
            //het geheel
            //mag vol lezen?

            await this.semaphore.waitLock('bouwplanbord'); //eigen lock
            this.api.block(true);
            this.cdf.detach(); //even geen change detection, want dat kost erg veel tijd
            PlanblockRij.clearHerberekenAgenda(); //hoeft niet meer
            //leeg de data voor een laatste detect, zodat oude rijen weggaan
            this.datums = [];
            this.datumstrings = []; //zelfde maar dan als format, eenvoudig
            this.dataGewijzigd = false; //meteen al
            this.itemsPerDateGefilterd = {}; //rebuild
            this.itemsPerDateAlles = {}; //rebuild voor overlap/ATW
            this.verwijderdeItems = []; //de verwijderde planitems
            //reset the personeeldata array voor invulling en invullen
            //we stoppen hier elk personeelslid in uit de juiste wm's
            // en vullen in hun tags de pi's voor hun diensten én voor hun mogelijke diensten
            //daarbij stoppen we dan flags voor heeft_pi en heeft_mogelijke_pi
            this.personeeldata = [];
            this.klanten = [];
            this.projectdata = [];
            this.ux.actieveGroepen = [];

            //cleanup oude rijen in de DOM
            this.cdf.detectChanges(); //nu uitvoeren
            await this.pauze(5);  //we doen dit voor het ophalen van de data, zodat de boel async wordt geschoond. Tegen "Failed to execute 'insertBefore' on 'Node'"

            //en nu aan het werk

            if (this.datumbereik !== 0) {
                this.einddatum = this.begindatum.clone().add(this.datumbereik - 1, 'days');
            }
            //else: manually set einddatum
            let numdagen = this.einddatum.diff(this.begindatum, 'days') + 1;

            //set daterange
            this.ux.daterange = [
                this.begindatum.toDate(),
                this.einddatum.toDate()
            ];

            for (let dag = 0; dag < numdagen; dag++) {
                let datum = this.begindatum.clone().add(dag, 'days');
                this.datums.push(datum);
                this.datumstrings.push(datum.format('YYYY-MM-DD'));
            }

            let eerstedatum = this.datumstrings[0];

            //we willen dagen ervoor en erna ivm overlapcheck (ATW)
            let dagervoor = this.datums[0].clone().subtract(1, 'day').format('YYYY-MM-DD'); //dan kan de eindtijd op de eerste dag zijn
            let laatstedatum = this.datumstrings[numdagen - 1];
            let dagerna = this.datums[numdagen - 1].clone().add(1, 'day').format('YYYY-MM-DD');
            let alledatums = [dagervoor, ...this.datumstrings, dagerna]; //voor loops intern. this.datumstrings is wat er wordt afgebeeld

            //we cachen een stuk minder, per week ipv maand, voor sneller reageren
            //let op: allerlei code (bijv ophalen afwezigheid) gaat er wel vanuit dat dit meer is dan de afgebeelde datums
            let periodStart = moment(dagervoor).startOf('week');
            let periodEnd = moment(dagerna).endOf('week');


            const planitems = await Planitem.getAll([Planitem, PlanitemFunction, PersoneelPlanitem], [
                [Planitem, {
                    where: {
                        startdate: {
                            $lte: periodEnd.format('YYYY-MM-DD'),
                            $gte: periodStart.format('YYYY-MM-DD')
                        }
                    },
                    order: ['startdate', 'starttime', 'endtime'],
                    softdeleted: true //ook de verwijderde
                }
                ],
            ]);
            this.log.debug(`**** PLANITEMS`, planitems, planitems[0]?._owner);

            // Externe planitems identificeren en hun originele projectinfo ophalen
            const externePlanitemIds = planitems.filter(pi => pi.externe_werkmaatschappij === this.api.werkmaatschappij.id && !this.projecten[pi.project_id]).map(pi => pi.id);
            this.externeProjectInfo = {};
            if (externePlanitemIds.length > 0) {
                try {
                    this.externeProjectInfo = (await this.api.cachedSend('planning', 'externe_projectinfo', {planitems: externePlanitemIds}, ['planitem', 'project'])).result;
                } catch (e) {
                    this.log.error('Fout bij ophalen externe projectinfo', e);
                }
            }

            //en de afwezigheden, als we toestemming hebben
            const afwezigheid =
                await PersoneelAfwezigheid.getAll([], [[
                    PersoneelAfwezigheid,
                    {
                        where: {
                            startdatum: {
                                $lte: periodEnd.format('YYYY-MM-DD'), //genoeg speling voor ook de volgende dag (voor als eindtijd op de volgende dag valt
                                // $gte: periodStart.format('YYYY-MM-DD')
                            },
                            einddatum: {
                                $or: [
                                    null,
                                    {$gte: periodStart.format('YYYY-MM-DD')}
                                ]
                            }
                        },
                    }
                ]]);


            //set metadata etc
            //a lot of metadata is per date, so we create a default hash for cloning
            //and fill itemsPerDate with empty arrays
            let perdate: hash<Planitem[]> = {}; //om te klonen
            for (let datum of alledatums) {
                perdate[datum] = []; //datumblokken
                this.itemsPerDateGefilterd[datum] = []; //planitems (zichtbaar)
                this.itemsPerDateAlles[datum] = []; //alle planitems (voor overlap/ATW)
            }
            //init al het personeel
            this.personeeldata = this.personeellijst.filter(p =>
                p.actief &&
                (p.werkmaatschappij_id === this.api.werkmaatschappij.id)
            ); //al het actieve personeel uit de juiste wms sowieso toevoegen
            //tags voor al het personeel, niet alleen het gefilterde, want er volgt later nog een toevoeging
            for (let p of this.personeellijst) {
                p._tags['itemsperdate'] = cloneDeep(perdate); //items per date
                p._tags['mogelijke_itemsperdate'] = cloneDeep(perdate); //mogelijke items per date
                p._tags['alle_items'] = []; //alle items, echt of mogelijk. Voor overlapcheck
                p._tags['alle_items_concreet'] = []; //de echte
                p._tags['alle_items_virtueel'] = [];
                p._tags['heeft_pi'] = p._tags['heeft_mogelijke_pi'] = false; //nog niet
                p._tags['leveranciers'] = {}; //hash boolean op leverancier id, voor leverancierfilter
                //afwezigheid in het personeel
                p.personeel_afwezigheid = afwezigheid.filter(a => a.personeelvast_id === p.id);
            }


            //reset de projecttags
            for (let project of this.projectlijst) {
                project._tags['heeft_pi'] = false;
                project._tags['functionlist'] = [];
            }

            //leveranciers
            for (let l of this.leverancierlijst) {
                l._tags['heeft_pi'] = l._tags['heeft_mogelijke_pi'] = false;
            }

            //filter projects for availability in this period
            const projecten = this.projectlijst.filter(project => !(project.begindatum > laatstedatum || (project.einddatum && project.einddatum < dagervoor)));

            //klanten en metadata regelen en daarna sorteren
            //doel van het sorteren is dat er weinig verplaatst bij bladeren

            for (let project of projecten) {
                let klantnaam = project.klantnaam || '?';
                let klantdata = this.klanten.find(klant => klant.klantnaam === klantnaam);
                if (!klantdata) {
                    //nieuw!
                    klantdata = {
                        klantnaam: klantnaam,
                        projecten: [],
                        projectid: {}
                    };
                    this.klanten.push(klantdata);
                }
                //we blijven af van ux.klantnaamOpened, want standaard dicht
                klantdata.projecten.push(project); //erin
                klantdata.projectid[project.id] = true; //snel zoeken
                if (!(project.id in this.ux.projectOpened)) {
                    this.ux.projectOpened[project.id] = true; //standaard open
                }

                //lijst met alle (gecombineerde) functies die moeten worden getoond tag->info
                let functiontags: hash<hash<any>> = {};

                //check alle items
                // We nemen lokale items voor dit project, EN externe items die bij dit placeholder-project horen
                const relevantePlanitems = planitems.filter(pi => {
                    if (pi.project_id === project.id) {
                        return true;
                    }
                    // Is dit een extern item dat bij de werkmaatschappij van dit placeholder-project hoort?
                    return project.externe_werkmaatschappij && this.externeProjectInfo[pi.id]?.werkmaatschappij === project.externe_werkmaatschappij;
                });

                for (let pi of relevantePlanitems) {
                    //zet de owner, want we hebben apart opgehaald
                    pi.project = this.projecten[pi.project_id] || project; // Gebruik het placeholder-project als fallback
                    //vul en tag het personeel en functies op de items
                    pi.setPersoneel(this.personeel);
                    pi.setFunctionlabel(this.functies);
                    pi.setKritiekLeverancierfilter(this.isLeverancier ? this.api.userdata.leverancier_id : null); //een leverancier ziet alleen kritiek over zijn eigen mensen

                    let ft = pi.functiontag;
                    //add this one?
                    if (ft) //relevante date?
                    {
                        let blockdate: string;
                        if (pi.startdate === dagervoor && pi.endtime < pi.starttime) {
                            //will be shown on the first date
                            blockdate = eerstedatum;
                            pi._tags.startRelative = '0'; //percentage
                            pi._tags.endRelative = moment(`${eerstedatum} ${pi.endtime}`).diff(this.datums[0], 'hours', true) / 24; //TODO: DST switch
                        } else if (this.datumstrings.includes(pi.startdate)) {
                            //niet via perdate, want daar zit de datum voor en na de periode in
                            //normal date
                            blockdate = pi.startdate;
                            pi._tags.startRelative = moment(`${pi.startdate} ${pi.starttime}`).diff(`${pi.startdate} 00:00`, 'hours', true) / 24;
                            if (pi.endtime > pi.starttime) {
                                pi._tags.endRelative = moment(`${pi.startdate} ${pi.endtime}`).diff(`${pi.startdate} 00:00`, 'hours', true) / 24;
                            } else {
                                pi._tags.endRelative = moment(`${pi.startdate} ${pi.endtime}`).add(1, 'day').diff(`${pi.startdate} 00:00`, 'hours', true) / 24;
                            }
                        }
                        if (blockdate) {
                            if (pi.isDeleted) {
                                //deze hoeft verder niet. Is een verwijderde
                                this.verwijderdeItems.push(pi);
                                continue;
                            }

                            // Altijd opnemen voor overlap/ATW
                            this.itemsPerDateAlles[blockdate].push(pi);

                            // Bepaal zichtbaarheid o.b.v. filtertool: filteren gebeurt op hele planitems
                            // In 'invulling_planning' én in 'project' negeren we personeel-/labelregels,
                            // zodat diensten zichtbaar blijven op basis van hun eigen kenmerken (zoals functie).
                            const zichtbaar = (!this.heeftActiefFilter) || (
                                (this.ux.weergave === 'invulling_planning' || this.ux.weergave === 'project')
                                    ? (this.filtertool?.filterPlanitemZonderPersoneel(pi) === true)
                                    : (this.filtertool?.filterPlanitem(pi) === true)
                            );

                            if (zichtbaar) {
                                //deze gaat getoond worden, dus de klant en het project hebben een relevant item
                                project._tags['heeft_pi'] = true; //deze ook

                                //will be shown, add the function tag
                                if (ft && (!functiontags[ft])) {
                                    //add this one, creating a description
                                    let mainf = pi.mainFunction;
                                    if (mainf) {
                                        let names = [];
                                        if (pi.isCombined) {
                                            //there are more functions
                                            names = pi.extraFunctions.map(function_id => (this.functies[function_id]?.naam || '(onbekende functie)'));
                                            names.sort((a, b) => a.localeCompare(b));
                                        }
                                        names.unshift(this.functies[mainf]?.naam || '(onbekende functie)'); //eerste is de main
                                        functiontags[ft] = {
                                            label: names.join(' / '),
                                            main: mainf,
                                            extra: [...pi.extraFunctions],
                                            functionalias: pi.functionalias,
                                            perdate: cloneDeep(perdate), //items per date
                                            boekbaar: !!(this.functies[mainf]?.functie_exact?.length), //is dit gekoppeld aan exact?
                                            items: [] //all items, for looping and finding etc
                                        };
                                    }
                                }
                                //zet een goede title + functiontitle
                                pi._tags['title'] = ''; //string maken
                                if (pi.leverancier_id && this.volLezen) {
                                    //is er een leverancier die mag vullen, dan tonen we hem
                                    pi._tags['title'] += `[In te vullen door: ${this.leveranciers[pi.leverancier_id]?.naam}] `;
                                }
                                pi._tags['title'] += `${project.label} ${moment(pi.startdate).format('dd D-M')} ${pi.starttime} - ${pi.endtime} ${functiontags[ft].label} `;
                                pi._tags['functiontitle'] = functiontags[ft].label;
                                if (functiontags[ft].functionalias) {
                                    pi._tags['title'] += ` (${functiontags[ft].functionalias}) `;
                                    pi._tags['functiontitle'] += ` (${functiontags[ft].functionalias}) `;
                                }
                                pi._tags['title'] += `${pi.implemented} / ${pi.capacity}`;

                                //and a functiontitle

                                functiontags[ft].perdate[blockdate].push(pi);
                                functiontags[ft].items.push(pi);
                                this.itemsPerDateGefilterd[blockdate].push(pi);
                            }

                            //ook toevoegen aan personeel
                            //aangezien pi.personeel gevuld is met items uit this.personeel
                            //heeft het item in pi.personeel dezelfde objectidentity als in personeeldata
                            for (let ppi of pi.personeel_planitem) {
                                let p = ppi.personeel; //shortcut
                                if (!p) {
                                    // this.log.error('PPI zonder personeel', ppi);
                                    continue;
                                }
                                //already in personeel?
                                if (!this.personeeldata.includes(p)) {
                                    this.personeeldata.push(p); //now added
                                }
                                // Voor overlap-detectie altijd de concrete items meenemen
                                p._tags['alle_items'].push(pi);
                                p._tags['alle_items_concreet'].push(pi);

                                if (zichtbaar) {
                                    // Alleen voor weergave de date-indexering en flags zetten
                                    p._tags['itemsperdate'][blockdate].push(pi);
                                    p._tags['heeft_pi'] = true;
                                    //is er een leverancier?
                                    let leverancier_id = ppi.leverancier_id || p.leverancier_id; //de afwijkende of de standaard, of niks
                                    //dus de leverancier ook
                                    if (leverancier_id && this.leveranciers[leverancier_id]) {
                                        this.leveranciers[leverancier_id]._tags['heeft_pi'] = true;
                                        p._tags['leveranciers'][leverancier_id] = true; //zodat we op leverancier kunnen filteren
                                    }
                                }
                            }

                            //en nu het andere personeel. Wie zou dit mogen
                            //dit gaat dus alleen om items die niet volledig zijn ingevuld
                            if (pi.implementationDegree < 0) {
                                for (let p of this.personeellijst) {
                                    if ((!p.actief) || (p.werkmaatschappij_id !== project.werkmaatschappij_id) || (pi.personeel.includes(p))) //mag niet of personeel doet deze dienst al
                                    {
                                        continue;
                                    }
                                    //alle actief personeel zit al in personeeldata, dus deze ook
                                    //als het personeel de juiste functie heeft, op de dienstdatum gecertificeerd is en niet afwezig is, dan moet hij aangeboden worden bij invullen
                                    if (pi.functions.every(fid => p.heeftFunctieGeldigOp(fid, pi.startdate))
                                        && p.certificeringGeldigOp(pi.startdate)
                                        && (!p.afwezigOp(pi.startdate, pi.starttime || '00:00', pi.endtime || '00:00'))) {
                                        // Voor overlap-detectie: altijd toevoegen aan alle_items (virtueel)
                                        p._tags['alle_items'].push(pi); //is er nog niet, want pi.personeel heeft dit personeeslid niet
                                        p._tags['alle_items_virtueel'].push(pi); //even speciaal

                                        if (zichtbaar) {
                                            // Alleen voor weergave invullen de mogelijke items tonen
                                            p._tags['mogelijke_itemsperdate'][blockdate].push(pi);
                                            p._tags['heeft_mogelijke_pi'] = true;
                                            //check expliciet of de leverancier wel bekend is, want niet iedere planner heeft leveranciertoestemming
                                            if (p.leverancier_id && this.leveranciers[p.leverancier_id]) {
                                                this.leveranciers[p.leverancier_id]._tags['heeft_mogelijke_pi'] = true; //hier wel alleen de standaardleverancier
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                //sorteer op opgeslagen volgorde, als dat er niet is komt het achteraan
                let metvolgorde: hash<any>[] = [];
                let zondervolgorde: hash<any>[] = [];
                for (let ft of Object.keys(functiontags)) {
                    let fl = {
                        label: functiontags[ft].label,
                        tag: ft,
                        main: functiontags[ft].main,
                        extra: functiontags[ft].extra,
                        functionalias: functiontags[ft].functionalias,
                        sorttag: `${functiontags[ft].label}${functiontags[ft].functionalias ?? ''}`,
                        perdate: functiontags[ft].perdate,
                        rows: 1, //how many rows do we need?
                        items: functiontags[ft].items,
                        boekbaar: functiontags[ft].boekbaar
                    };
                    if (this.functievolgorde[project.id] && this.functievolgorde[project.id].includes(ft)) {
                        metvolgorde.push(fl);
                    } else {
                        zondervolgorde.push(fl);
                    }
                }
                metvolgorde.sort((a, b) => this.functievolgorde[project.id].indexOf(a.tag) > this.functievolgorde[project.id].indexOf(b.tag) ? 1 : -1);
                //en zondervolgordeinfo op label erachter
                zondervolgorde.sort((a, b) => a.sorttag.localeCompare(b.sorttag));

                project._tags['functionlist'] = [...metvolgorde, ...zondervolgorde];

                //calculate number of rows per function.
                //we use the fact that the planitems are sorted on date and time
                //and we have them in functionlist[].items
                for (let ft of project._tags['functionlist']) {
                    //stupid loops are better than bad smart ones
                    //we check for a spot for every item
                    for (let i = 0; i < ft.items.length; i++) {
                        let pi = ft.items[i];
                        let yesterday = moment(pi.startdate).subtract(1, 'day').format('YYYY-MM-DD');
                        pi._tags.row = 0;
                        let overlap = false;
                        do {
                            //check if there is an item before this one
                            //that is on this row and overlaps
                            overlap = false;
                            for (let prev_i = i - 1; prev_i >= 0; prev_i--) {
                                let prev = ft.items[prev_i];
                                //too old?
                                if (prev.startdate < yesterday) {
                                    break; //the rest is even older, so the current row is fine
                                }
                                //not on this row?
                                if (prev._tags.row !== pi._tags.row) {
                                    continue; //irrelevant
                                }
                                //check op overlap
                                //planitem cachet zelf
                                if (pi.overlap(prev)) {
                                    //overlap!
                                    overlap = true;
                                    pi._tags.row++; //try next row
                                    break;
                                }
                            }
                        } while (overlap);
                        //do we have enough rows?
                        ft.rows = Math.max(ft.rows, pi._tags.row + 1);
                    }
                    //old fashioned loop
                    //add an empty array for looping over these rows
                    ft.rowlist = Array(ft.rows).fill(0).map((_x, i) => i); //row number in the array
                }
            }

            this.projectdata = projecten; //after transforming
            //sorteer klanten
            this.klanten.sort((k1, k2) => k1.klantnaam.localeCompare(k2.klantnaam));
            //sort personeel
            this.personeeldata.sort((p1, p2) => p1.naam.localeCompare(p2.naam));

            //regel groepen en overlap

            let personeelsgroepen = {}; //welke groepen zijn actief?
            for (let person of this.personeeldata) {
                personeelsgroepen[person.groep] ||= person._tags['heeft_pi'];
                person._tags['invulrijen'] = 1; //hoeveel rijen nodig op invulling?
                //we moeten de items opnieuw sorteren, omdat ze over projecten heengegaan zijn
                person._tags['alle_items'].sort((pi1, pi2) =>
                    `${pi1.startdate} ${pi1.starttime}` < `${pi2.startdate} ${pi2.starttime}` ? -1 : 1
                );
                //dit alleen als weergave invullen is. LET OP: dan moet er dus herbouwd worden
                // if (this.ux.weergave === 'invulling_planning') {
                for (let i = 0; i < person._tags['alle_items'].length; i++) {
                    let pi = person._tags['alle_items'][i];

                    //invulrij moet per persoon, want het item wordt vaker afgebeeld
                    pi._tags['invulrij'] ||= {};

                    //is dit virtueel, en overlapt het met concreet (inclusief 8 uur pauze)
                    if (person._tags['alle_items_virtueel'].includes(pi) && person._tags['alle_items_concreet'].some(pi2 => pi2.overlap(pi, 480)
                    )) {
                        //rij -1 betekent niet afbeelden
                        pi._tags['invulrij'][person.id] = -1;
                        continue; //volgende
                    }

                    let yesterday = moment(pi.startdate).subtract(1, 'day').format('YYYY-MM-DD');
                    pi._tags['invulrij'][person.id] = 0;
                    let overlap = false;
                    do {
                        //weer op zoek naar overlap, net als bij functies hierboven
                        overlap = false;
                        for (let prev_i = i - 1; prev_i >= 0; prev_i--) {
                            let prev = person._tags['alle_items'][prev_i];
                            //te oud?
                            if (prev.startdate < yesterday) {
                                break; //de rest is nog ouder, dus de huidige rij is prima
                            }
                            //not on this row?
                            if (prev._tags['invulrij'][person.id] !== pi._tags['invulrij'][person.id]) {
                                continue; //irrelevant
                            }
                            //could overlap?
                            if (pi.overlap(prev)) {
                                //overlap!
                                overlap = true;
                                pi._tags['invulrij'][person.id]++; //probeer de volgende
                                break;
                            }
                        }
                    } while (overlap);
                    //hoeveel rijen in de persoon
                    person._tags['invulrijen'] = Math.max(person._tags['invulrijen'], pi._tags['invulrij'][person.id] + 1);
                }
                //even een array met het aantal rijen
                person._tags['invulrijlijst'] = Array(person._tags['invulrijen']).fill(0).map((_x, i) => i); //row number in the array
            }
            // }


            //actievegroepen verzamelen,
            //op gewenste volgorde
            this.ux.actieveGroepen = ['Personeel', 'ZZP Pool']; //Sprint 10: deze groepen sowieso altijd zichtbaar
            for (let groep of Personeel.PersoneelsGroepen) {
                if ((!this.ux.actieveGroepen.includes(groep)) && personeelsgroepen[groep]) {
                    this.ux.actieveGroepen.push(groep);
                }
            }

            //check overlap

            let planitemsvoor: Planitem[] = [];
            let planitemshuidig: Planitem[] = [];
            let planitemsna: Planitem[] = [];
            for (planitemsna of Object.values(this.itemsPerDateAlles)) {
                //we berekenen de overlap in planitemshuidig, met de gegeven items erbij, zodat we ook naar gisteren en morgen kijken
                const checkitems = [...planitemsvoor, ...planitemshuidig, ...planitemsna];
                for (let pi of planitemshuidig) {
                    pi.checkPersoneeloverlap(checkitems);
                }
                //en doorschuiven
                planitemsvoor = planitemshuidig;
                planitemshuidig = planitemsna;
            }


            //haal nu de comments op

            // await this.pauze(5); //test
            await this.haalComments();

            //and change the now line
            this.bepaalNu();

            this.log.debug(`externe planitems`, planitems.filter(pi => pi.externe_werkmaatschappij === this.api.werkmaatschappij.id))

            // 
            this.cdf.detectChanges();
            await this.pauze(5); //geef zonejs de tijd
            // 
        } catch (e) {
            this.log.error(e);
            this.closeWindow();
        } finally {
            this.api.unblock();
            this.semaphore.unlock('bouwplanbord');
            this.cdf.reattach(); //we gaan weer door met de change
        }
    }

    /**
     * Helper voor planbord, zodat er niets steeds heel veel ifs staan, maar ? gebruikt kan worden
     * @param pid
     * @param datum
     */
    personeelcommentVoor(pid: number, datum: string): PersoneelDatumcomment | null {
        return this.personeeldatumcomments[pid] && this.personeeldatumcomments[pid][datum] || null;
    }

    /**
     * Open een editor op een comment bij een personeelslid en een datum
     * @param pid
     * @param datum
     */
    editPersoneelcomment(pid: number, datum: string) {
        if (!this.api.can('personeelvast_datumcomment', 'full')) {
            this.log.warn(`Poging bewerken comment zonder toestemming`);
            return;
        }
        let record = this.personeelcommentVoor(pid, datum);
        //of nieuw?
        if (!record) {
            record = PersoneelDatumcomment.createNew();
            record.personeelvast_id = pid;
            record.datum = datum;
        }
        void this.createWindow('PersoneelcommentEdit', {
            editRecord: record,
            naam: this.personeel[pid].naam,
            datum: moment(datum).format('DD-MM-YYYY')
        }, true, true);
    }

    /**
     * Helper voor planbord, zodat er niets steeds heel veel ifs staan, maar ? gebruikt kan worden. Haal een projectdatumcomment op voor een project, functietag en datum
     * @param projectid
     * @param ft
     * @param datum
     */
    projectcommentVoor(projectid: number, ft: string, datum: string): ProjectDatumcomment | null {
        return this.projectdatumcomments[projectid] && this.projectdatumcomments[projectid][ft] && this.projectdatumcomments[projectid][ft][datum] || null;
    }

    /**
     * Open een editor op een comment bij een project, functietag en datum
     * @param projectid
     * @param fd
     * @param datum
     */
    editProjectcomment(projectid: number, fd: hash<any>, datum: string) {
        if (!this.canComment) {
            return;
        }
        let record = this.projectcommentVoor(projectid, fd.tag, datum);
        //of nieuw?
        if (!record) {
            record = ProjectDatumcomment.createNew();
            record.project_id = projectid;
            record.functietag = fd.tag;
            record.datum = datum;
        }

        //zoek even de naam
        const pr = this.projectdata.find(pr => pr.id === projectid);
        if (!pr) {
            return; //is er niet
        }
        let naam = `${pr.naam} - ${fd.label}`;
        if (fd.functionalias?.length) {
            naam += ` - ${fd.functionalias}`;
        }

        void this.createWindow('ProjectcommentEdit', {
            editRecord: record,
            naam: naam,
            datum: moment(datum).format('DD-MM-YYYY')
        }, true, true);
    }

    async openVerwijderdePlanitems() {
        //hier moeten de logs wel even gehaald worden
        for (let item of this.verwijderdeItems) {
            await item.vulLog();
        }
        await this.createWindow('VerwijderdePlanitems', {verwijderd: this.verwijderdeItems}, true, true);
    }

    /////////////////////////////// Planbord helpers
    toonKlantInOverzicht(klantdata: any): boolean {
        //een klant heeft pi als hij binnen de huidige wm-selectie een project heeft dat een pi heeft
        //LET OP: in de basisweergave slaan we projecten met een externe werkmaatschappij over
        return klantdata.projecten.some((pr: Project) => (!pr.externe_werkmaatschappij) && (this.ux.projectfilter ? pr.id === this.ux.projectfilter : pr._tags['heeft_pi']));
    }

    /**
     * Geeft de naam terug behorend bij een externe werkmaatschappij-id.
     * Wordt gebruikt in de rij "Andere werkmaatschappijen".
     */
    getWerkmaatschappijNaam(id: number | null | undefined): string {
        if (!id) {
            return 'Andere werkmaatschappij';
        }
        return this.werkmaatschappijen?.[id]?.naam || 'Andere werkmaatschappij';
    }

    /**
     * Lijst met externe projecten die volgens de huidige weergavelogica getoond moeten worden.
     * - Als er geen projectfilter is: toon alleen projecten met een pi in de huidige periode (heeft_pi)
     * - Als er wel een projectfilter is: toon enkel dat project (ook als het extern is)
     */
    externeProjectenVoorWeergave(): Project[] {
        const lijst = this.projectlijst.filter(p => !!p.externe_werkmaatschappij && (((!this.ux.projectfilter) && !!p._tags['heeft_pi']) || p.id === this.ux.projectfilter));
        return sortBy(lijst, p => this.getWerkmaatschappijNaam(p.externe_werkmaatschappij) + ' ' + (p.label || p.naam || ''));
    }

    /**
     * Helper bij de medewerker-weergave: mag dit personeelslid afgebeeld worden
     * @param personeel
     * @param groep De groep waar deze persoon zou worden afgebeeld
     */
    weergaveMedewerkerFilterPersoneel(personeel: Personeel, groep: Personeelgroep): boolean {
        //we checken vooral wat niet mag, als iemand daar doorkomt, dan mag het wel

        //groep
        if (personeel.groep !== groep) {
            return false;
        }

        //extern personeel alleen met pi
        if (groep === 'Extern' && !personeel._tags['heeft_pi']) {
            return false;
        }

        //klopt leverancier?
        if (this.ux.leverancierfilter) {
            if (this.ux.leverancierfilter === -1 && groep !== 'Personeel') {
                return false;
            } else if (this.ux.leverancierfilter === -2 && groep !== 'ZZP Pool') {
                return false;
            } else if (this.ux.leverancierfilter === -3 && groep !== 'ZZP') {
                return false;
            } else if (this.ux.leverancierfilter === -4 && groep !== 'Extern') {
                return false;
            }
            //anders is het filter een specifieke id
            else if (this.ux.leverancierfilter > 0 && (!personeel._tags['leveranciers'][this.ux.leverancierfilter])) {
                return false;
            }
        }

        // filtertool: pas personeelsfilter toe als er een actieve filter is
        if (this.heeftActiefFilter) {
            if (!this.filtertool?.filterPersoneel(personeel)) {
                return false;
            }
        }

        //anders prima
        return true;
    }

    /**
     * Filter op personeel bij invullen
     * @param personeel
     */
    weergaveInvullenFilterPersoneel(personeel: Personeel): boolean {
        // Als er een actief filter is, pas dan de personeelsfilter toe en toon uitsluitend
        // de personen die matchen. Hierbij negeren we de heeft_pi/heeft_mogelijke_pi-voorwaarde,
        // zodat ook een geselecteerd personeelslid zonder diensten zichtbaar blijft.
        if (this.heeftActiefFilter) {
            return !!this.filtertool?.filterPersoneel(personeel);
        }

        // Zonder actief filter: toon alleen personeel dat concrete of mogelijke diensten heeft.
        if (!(personeel._tags['heeft_pi'] || personeel._tags['heeft_mogelijke_pi'])) {
            return false;
        }

        // Klopt leverancier?
        if (this.ux.leverancierfilter) {
            switch (this.ux.leverancierfilter) {
                case -1:
                    if (personeel.groep !== 'Personeel') {
                        return false;
                    }
                    break;
                case -2:
                    if (personeel.groep !== 'ZZP Pool') {
                        return false;
                    }
                    break;
                case -3:
                    if (personeel.groep !== 'ZZP') {
                        return false;
                    }
                    break;
                case -4:
                    if (personeel.groep !== 'Extern') {
                        return false;
                    }
                    break;
                default:
                    if (this.ux.leverancierfilter > 0 && personeel.leverancier_id !== this.ux.leverancierfilter) {
                        return false;
                    }
                    break;
            }
        }

        // Anders prima
        return true;
    }

    //// verwijderde diensten

    /**
     * Create a new item to the planbord, defaulting to the given project and possibly functiondescription, and open the editor for adding them
     */
    async addPlanItem(project: Project, date: string, daypart: 'night' | 'morning' | 'evening', functiondesc?: any) {
        let pi = Planitem.createNew(project);
        //also set the project_id
        pi.project_id = project.id;

        pi.startdate = date;
        switch (daypart) {
            case 'morning':
                pi.starttime = '07:00';
                pi.endtime = '15:00';
                break;
            case 'evening':
                pi.starttime = '15:00';
                pi.endtime = '23:00';
                break;
            case 'night':
                pi.starttime = '23:00';
                pi.endtime = '07:00'; //next day
                break;
        }
        //functiondesc comes from the functionlist we generated in buildPlanboard. It contains the main function and extra functions
        if (functiondesc) {
            pi.planitem_function = [PlanitemFunction.createNew(pi, {functie_id: functiondesc.main, main: true}),
                ...(functiondesc.extra.map(fid => PlanitemFunction.createNew(pi, {functie_id: fid, main: false})))
            ];
            pi.functionalias = functiondesc.functionalias;
        }
        pi._tags.uitPlanbord = true;

        void this.createWindow('Planitemedit', {
            editRecord: pi,
            projects: this.projectdata,
            allitemsPerdate: this.itemsPerDateAlles,
            filtertoolId: this.ux.filterActief ? this.filtertool?.id : null
        }, true, true);
        //en achter de schermen dit project even open
        this.ux.projectOpened[project.id] = true;
    }

    /**
     * Try to scroll to the given item
     * @param item
     */
    gotoPlanitem(item: Planitem) {
        //open alles

        //we zoeken project en klant en zetten die op open
        let project = this.projectdata.find(pr => pr.id === item.project_id);
        if (project) {
            this.ux.projectOpened[project.id] = true;
            this.ux.klantnaamOpened[project.klantnaam] = true;
        }
        //de rest doen we zometeen
        setTimeout(_ => {
                //find the block;
                let block = this.planblocks.find(pb =>
                    pb.planitem === item && !pb.visual //content version
                );
                if (!block) {
                    this.log.warn('Missing block');
                    return;
                }
                //lets get the position in the scroller
                let scrollerelement = this.planbordscroller.nativeElement;
                let blockelement = block.nativeElement;

                let left = 0, top = 0;
                while (blockelement && blockelement !== scrollerelement) {
                    left += blockelement.offsetLeft;
                    top += blockelement.offsetTop;
                    blockelement = blockelement.offsetParent;
                }

                //now make sure that position is centered given the width minus the width of de sticky column
                left -= (scrollerelement.clientWidth - this.naamcel.nativeElement.clientWidth) / 2;
                //same for height
                top -= (scrollerelement.clientHeight - this.planbordheader.nativeElement.clientHeight) / 2;
                this.planbordscroller.nativeElement.scrollTo({left: left, top: top, behavior: 'smooth'});
                block.attention();
            }
            , 200);
    }

    async editPlanitem(item: Planitem) {
        //log halen we wel op als de logtab geklikt wordt
        //open het editwindow als semi-modal

        // We moeten de externe projectinfo even opzoeken voor dit specifieke item
        // Deze info is bij de bouw van het planbord opgehaald, maar we hebben hier geen directe referentie meer naar de lokale variabele.
        // Echter, we hebben in bouwPlanbord de informatie al in externeProjectInfoPerPlanitem.
        // We kunnen de info ook afleiden of opnieuw ophalen, maar het mooiste is als we het doorgeven.
        // Voor nu halen we het op uit de cache of sturen we het mee als we het nog hebben.
        // Gezien editPlanitem vanuit de template wordt aangeroepen, is het lastig om de hash hier te hebben zonder property.
        
        // Ik voeg een property toe aan Planbord om deze info vast te houden.
        
        await this.createWindow('Planitemedit', {
            editRecord: item,
            projects: this.projectdata,
            externeProjectInfo: this.externeProjectInfo[item.id],
            allitemsPerdate: this.itemsPerDateAlles,
            filtertoolId: this.ux.filterActief ? this.filtertool?.id : null
        }, true, true);
    }

    /**
     * Open het multi-invullingsscherm voor een functierij
     * @param fd De functiebeschrijving met items
     */
    async openMultiInvulling(fd: any) {
        if (this.isLeverancier || !this.magPersoneel || !fd.items?.length) {
            return;
        }

        const record = fd.items[0];

        // Bouw lookups hash voor het component
        const lookups = {
            functions: Object.values(this.functies),
            personeel: this.personeellijst,
            leverancier: this.leverancierlijst
        };

        const lookupHashes = {
            functions: this.functies,
            personeel: this.personeel,
            leverancier: this.leveranciers
        };

        await this.createWindow('PlanitemMultiInvulling', {
            record: record,
            lookups: lookups,
            lookupHashes: lookupHashes,
            filtertool: this.ux.filterActief ? this.filtertool : null,
            allitemsPerdate: this.itemsPerDateAlles
        }, true, true);
    }

    /**
     * Klik op een plus/min in item in invulling_planning
     * @param item
     * @param personeel
     * @param add
     */
    async planitemAddRemove(item: Planitem, personeel: Personeel, add: boolean) {
        if (!this.magPersoneel) {
            return;
        }
        let saveitem = item.fullClone();
        saveitem.planitem_function = null; //weg
        if (add) {
            saveitem.personeel_planitem.push(PersoneelPlanitem.createNew(item, {
                personeelvast_id: personeel.id,
                leverancier_id: personeel.leverancier_id
            }));
        } else {
            saveitem.personeel_planitem = saveitem.personeel_planitem.filter(ppi => ppi.personeelvast_id !== personeel.id);
        }
        await saveitem.save();
    }

    //////////////////////////////////////////  UX  /////////////////////////////////////////
    toggleKlantnaamOpened(klantnaam): void {
        this.ux.klantnaamOpened[klantnaam] = !this.ux.klantnaamOpened[klantnaam];
    }

    /**
     * open/sluit project zodat diensten wel of niet te zien zijn
     */
    toggleProjectOpened(project: Project): void {
        this.ux.projectOpened[project.id] = !this.ux.projectOpened[project.id];
    }

    toggleGroepOpened(groep: string): void {
        this.ux.groepOpened[groep] = !this.ux.groepOpened[groep];
    }

    /**
     * Met pijlen verplaatsen van functie
     * @param project
     * @param index Index in de huidige functionlist
     * @param richting naar achteren (+1) of voren (-1) in de array
     */
    async verplaatsfunctie(project: Project, index: number, richting: number): Promise<void> {
        const fl = project._tags['functionlist'];
        let andere = index + richting;
        if (andere < 0 || andere >= fl.length) {
            return;
        }
        let eerste = fl[index];
        fl[index] = fl[andere];
        fl[andere] = eerste;
        //tags ook in de functievolgorde, en dat opslaan
        this.functievolgorde[project.id] = project._tags['functionlist'].map(fl => fl.tag);
        await this.api.tag(`pl_functievolgorde_${this.api.werkmaatschappij.id}`, this.functievolgorde); //async
    }

    /**
     * Maak het verplaatsen met pijlen ongedaan
     * @param project
     * @param event
     */
    async heroderfuncties(project: Project, event: MouseEvent) {
        project._tags['functionlist'].sort((a, b) => a.sorttag.localeCompare(b.sorttag));
        delete this.functievolgorde[project.id]; //alles weer natuurlijk
        await this.api.tag(`pl_functievolgorde_${this.api.werkmaatschappij.id}`, this.functievolgorde); //async
        event.stopPropagation();
    }

    /**
     * Set de weergave via de knoppen en zorg dat de weergave-data opnieuw wordt opgebouwd.
     * Bij invulling_planning forceren we een weekbereik via switchPeriode(); bij andere weergaven
     * bouwen we direct opnieuw op zodat filters en zichtbaarheid correct toegepast blijven.
     */
    async setWeergave(weergave: 'project' | 'invulling' | 'invulling_planning') {
        if (this.ux.weergave !== weergave) {
            this.api.block(true);
            await this.pauze(20); //zonejs
            this.cdf.detach();
            await this.pauze(5); //zonejs
            PlanblockRij.clearHerberekenAgenda(); //kan stoppen
            this.ux.weergave = weergave;
            if (this.ux.leverancierfilter > 0) {
                this.ux.leverancierfilter = null;
            }
            // invulling_planning altijd op een week
            if (weergave === 'invulling_planning') {
                if (this.datumbereik !== 7) {
                    this.datumbereik = 7;
                    this.switchPeriode(); // triggert via setBegindatum() al een herbouw
                } else {
                    // week stond al goed: alsnog herbouw zodat context wisselt
                    await this.bouwPlanbord();
                }
            } else {
                // Andere weergaven: altijd herbouw zodat filters behouden blijven
                await this.bouwPlanbord();
            }
            this.api.unblock();
            this.cdf.detectChanges();
            await this.pauze(5);
            this.cdf.reattach();
        }
    }

    /**
     * Kijk of de huidige datum + tijd op het planbord staat
     */
    bepaalNu() {
        this.nu = moment(); //bijwerken
        //helpers
        this.nustring = this.nu.format('YYY-MM-DD HH:mm:ss');
        this.nudate = this.nu.format('YYYY-MM-DD');
        //we bepalen de exacte range op het planbord. Op dit moment is dat 0:00 begindatum tot 23:59 einddatum
        let beginmoment = this.begindatum.clone().startOf('day');
        let eindmoment = this.datums[this.datums.length - 1].clone().add(1, 'day').startOf('day'); //inclusief
        if (!this.nu.isBetween(beginmoment, eindmoment)) {
            this.nuLijn = null; //niet zichtbaar
        } else {
            //percentage in de tijd
            //let op zomer-/wintertijd
            let uren = eindmoment.diff(beginmoment, 'hours', true) || 1; //voorkom divide by zero
            this.nuLijn = this.nu.diff(beginmoment, 'hours', true) / uren; //float
        }
    }


    dragStart(e: DragEvent) {
        this.start_top = this.planbordscroller.nativeElement.scrollTop;
        this.start_left = this.planbordscroller.nativeElement.scrollLeft;
        this.start_x = e.clientX;
        this.start_y = e.clientY;
        e.dataTransfer.setDragImage(this.planbordscroller.nativeElement, e.clientX, e.clientY);
        e.dataTransfer.dropEffect = 'move';
    }

    dragEnd(e: DragEvent) {
        const dy = e.clientY - this.start_y;
        const dx = e.clientX - this.start_x;
        this.planbordscroller.nativeElement.scrollTop = this.start_top - dy;
        this.planbordscroller.nativeElement.scrollLeft = this.start_left - dx;
    }


    /** reload **/
    /**
     * Reload data en herbouw planbord. Op verzoek of als we zelf iets gewijzigd hebben
     * @private
     */
    private async fullReload() {
        await this.haalBasisdata();
        await this.bouwPlanbord();
    }

    /**
     * Reset the planboard to top left
     * @private
     */
    private resetScroll() {
        this.planbordscroller.nativeElement.scrollTo(0, 0);
    }

    /**
     * Haal de comments voor de datums op, gegeven ons personeel
     * @private
     */
    private async haalComments() {
        this.personeeldatumcomments = {}; //legen
        this.projectdatumcomments = {}; //legen
        if (!this.volLezen) {
            return; //die mag dit niet
        }
        //personeeldatacomments
        const pids = (this.personeeldata || []).map(p => p.id);

        if (pids.length && this.datumstrings.length) {

            const comments: PersoneelDatumcomment[] = await PersoneelDatumcomment.getAll([],
                [[PersoneelDatumcomment, {
                    where: {
                        personeelvast_id: pids,
                        datum: {
                            $gte: this.datumstrings[0],
                            $lte: this.datumstrings[this.datumstrings.length - 1]
                        }
                    }
                }]]);

            this.personeeldatumcomments = {}; //legen, we doen de rest toch sync
            for (let comment of comments) {
                if (!this.personeeldatumcomments[comment.personeelvast_id]) {
                    this.personeeldatumcomments[comment.personeelvast_id] = {};
                }
                this.personeeldatumcomments[comment.personeelvast_id][comment.datum] = comment;
            }
        }

        //afwezigheid
        for (let p of this.personeellijst) {
            for (let a of p.personeel_afwezigheid || []) {
                for (let d of this.datumstrings) {
                    //nogal een dikke loop, maar er zijn weinig afwezigheden
                    if (a.afwezigOp(d)) {
                        if (!this.personeeldatumcomments[p.id]) {
                            this.personeeldatumcomments[p.id] = {};
                        }
                        if (!this.personeeldatumcomments[p.id][d]) {
                            //er moet een comment zijnn
                            this.personeeldatumcomments[p.id][d] = PersoneelDatumcomment.create({
                                personeelvast_id: p.id,
                                datum: d,
                                comment: '',
                                achterkleur: '#000000',
                                voorkleur: '#FFFFFF'
                            });
                        }
                        //en die taggen we
                        this.personeeldatumcomments[p.id][d]._tags['afwezig'] = a.isHeledagen ? 'afwezig' : `afwezig ${a.vanTotString}`;
                    }
                }
            }
        }

        //projectdatacomments
        const projectids = (this.projectdata || []).map(pr => pr.id);
        if (projectids.length && this.datumstrings.length) {

            const comments: ProjectDatumcomment[] = await ProjectDatumcomment.getAll([],
                [[ProjectDatumcomment, {
                    where: {
                        project_id: projectids,
                        datum: {
                            $gte: this.datumstrings[0],
                            $lte: this.datumstrings[this.datumstrings.length - 1]
                        }
                    }
                }]]);

            this.projectdatumcomments = {}; //de rest is sync
            for (let comment of comments) {
                if (!this.projectdatumcomments[comment.project_id]) {
                    this.projectdatumcomments[comment.project_id] = {};
                }
                if (!this.projectdatumcomments[comment.project_id][comment.functietag]) {
                    this.projectdatumcomments[comment.project_id][comment.functietag] = {};
                }
                this.projectdatumcomments[comment.project_id][comment.functietag][comment.datum] = comment;
            }
        }
    }

    /**
     * Opvangen notificatie die speciaal wordt gemaakt aan de achterkant
     * @param data
     * @private
     */
    private async personeelcommentUpdate(data: { id: number, datum: string, personeel: number, delete: boolean }) {
        if (!this.volLezen) {
            return; //die mag dit niet
        }
        if (this.datumstrings.includes(data.datum) && this.personeeldata.find(p => p.id === data.personeel)) {
            //deze notificatie is relevant
            const oldcomment = this.personeelcommentVoor(data.personeel, data.datum);
            if (data.delete && oldcomment) {
                //was er een afwezigheid?
                if (oldcomment._tags['afwezig']) {
                    //reset bestaande, maar bewaar hem wel
                    oldcomment.id = null;
                    oldcomment.comment = '';
                    oldcomment.voorkleur = '#FFFFFF';
                    oldcomment.achterkleur = '#000000';
                } else
                    //gewoon weg
                {
                    delete this.personeeldatumcomments[data.personeel][data.datum];
                }

            } else if (!data.delete) {
                const comment = await PersoneelDatumcomment.fromId(data.id);
                if (comment) {
                    if (!this.personeeldatumcomments[data.personeel]) {
                        this.personeeldatumcomments[data.personeel] = {};
                    }
                    this.personeeldatumcomments[data.personeel][data.datum] = comment;
                    if (oldcomment && oldcomment._tags['afwezig']) {
                        comment._tags['afwezig'] = oldcomment._tags['afwezig'];
                    }
                }
                //als we een update krijgen maar hem niet vinden, dan laten we hem staan
            }
        }
    }

    /**
     * Opvangen notificatie die speciaal wordt gemaakt aan de achterkant voor projectcomments
     * @param data
     * @private
     */
    private async projectcommentUpdate(data: { id: number, datum: string, project: number, functietag: string, delete: boolean }) {
        if (!this.volLezen) {
            return; //die mag dit niet
        }
        if (this.datumstrings.includes(data.datum) && this.projectdata.find(pr => pr.id === data.project)) {
            //deze notificatie is relevant
            if (data.delete && this.projectcommentVoor(data.project, data.functietag, data.datum)) {
                delete this.projectdatumcomments[data.project][data.functietag][data.datum];
            } else {
                const comment = await ProjectDatumcomment.fromId(data.id);
                if (comment) {
                    if (!this.projectdatumcomments[data.project]) {
                        this.projectdatumcomments[data.project] = {};
                    }
                    if (!this.projectdatumcomments[data.project][data.functietag]) {
                        this.projectdatumcomments[data.project][data.functietag] = {};
                    }
                    this.projectdatumcomments[data.project][data.functietag][data.datum] = comment;
                }
            }
        }
    }
}
